// BannerParosPendientesCausal — paros que abrió la MÁQUINA sin causal en la
// línea del líder (contrato de ingesta §7). La máquina sabe cuándo paró, no
// por qué: la persona completa la causal desde acá. Calca BannerParosAbiertos
// (mismo lugar, mismo tono de "acción pendiente").
//
// Presentacional: la página pasa los paros ya filtrados por línea y el
// callback. Con "Ver como" activo (`soloLectura`) se listan sin acción — el API
// respondería 403 a la escritura.
//
// CompletarCausalDialog lo reusan el líder (L2) y el supervisor.

import { useState } from 'react';
import type { CausalParoTreeNode, ParoCompletarCausal, ParoPendienteCausal } from 'schemas';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Icon } from '../../components/ui/icon';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { useTouchTargets } from '../../components/ui/touch-targets';
import { CausalPicker, causalEspecificaDe } from './CausalPicker';

/** Hora HH:MM de un timestamp de paro (hora local etiquetada Z — "UTC falso"). */
export function horaParo(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  // Se leen los campos UTC: es la hora de planta tal cual la guardó la BD.
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}

/** "06:10–06:32 · 22 min" o "desde 06:10 · en curso". */
export function rangoParo(p: Pick<ParoPendienteCausal, 'hora_inicio' | 'hora_fin' | 'minutos'>) {
  if (!p.hora_fin) return `desde ${horaParo(p.hora_inicio)} · en curso`;
  const min = p.minutos === null ? '' : ` · ${Math.round(p.minutos)} min`;
  return `${horaParo(p.hora_inicio)}–${horaParo(p.hora_fin)}${min}`;
}

export function BannerParosPendientesCausal({
  paros,
  soloLectura,
  onCompletar,
}: {
  paros: ParoPendienteCausal[];
  soloLectura: boolean;
  onCompletar: (paro: ParoPendienteCausal) => void;
}) {
  const touch = useTouchTargets();
  if (paros.length === 0) return null;
  return (
    <section
      aria-label="Paros de máquina sin causal"
      className="flex flex-col gap-2 rounded border border-warn bg-warn-soft p-3"
    >
      <div className="flex items-center gap-2 text-sm font-medium text-warn">
        <Icon name="plug" size={15} stroke={2} />
        <span>
          {paros.length === 1
            ? 'La máquina registró un paro sin causal'
            : `La máquina registró ${paros.length} paros sin causal`}
        </span>
      </div>
      <ul className="flex flex-col gap-2">
        {paros.map((p) => (
          <li
            key={p.id}
            className="flex items-center justify-between gap-3 rounded border border-border bg-surface-elevated px-3 py-2 text-sm"
          >
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-medium text-fg">
                {p.hora_fin
                  ? `Paro · ${rangoParo(p)}`
                  : `Paro en curso · desde ${horaParo(p.hora_inicio)}`}
              </span>
              <span className="truncate text-xs text-fg-tertiary">
                {p.linea_nombre} · {p.fecha}
                {p.ref_externa ? (
                  <>
                    {' · '}
                    <span className="font-mono">{p.ref_externa}</span>
                  </>
                ) : null}
              </span>
            </div>
            {soloLectura ? null : (
              <Button type="button" size={touch ? 'touch' : 'sm'} onClick={() => onCompletar(p)}>
                Asignar causal
              </Button>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

export interface CompletarCausalDialogProps {
  paro: ParoPendienteCausal;
  causales: CausalParoTreeNode[];
  guardando: boolean;
  /** Error del servidor (mensaje ya legible). */
  serverError: string | null;
  onGuardar: (body: ParoCompletarCausal) => void;
  onClose: () => void;
}

export function CompletarCausalDialog({
  paro,
  causales,
  guardando,
  serverError,
  onGuardar,
  onClose,
}: CompletarCausalDialogProps) {
  const touch = useTouchTargets();
  const [causalMadreId, setCausalMadreId] = useState('');
  const [causalId, setCausalId] = useState('');
  const [observaciones, setObservaciones] = useState(paro.observaciones ?? '');
  const [error, setError] = useState<string | null>(null);

  const especifica = causalEspecificaDe(causales, causalMadreId, causalId);
  const requiereObs = especifica?.requiere_observacion ?? false;

  function guardar() {
    setError(null);
    if (!causalMadreId) return setError('Elija una categoría');
    if (!causalId) return setError('Elija una causal específica');
    const obs = observaciones.trim();
    if (requiereObs && obs.length < 5) {
      return setError('Observación obligatoria (mínimo 5 caracteres) para esta causal');
    }
    onGuardar({
      causal_madre_id: causalMadreId,
      causal_id: causalId,
      ...(obs ? { observaciones: obs.slice(0, 500) } : {}),
    });
  }

  return (
    <Dialog open onClose={onClose}>
      <DialogHeader>
        <DialogTitle>Asignar causal al paro</DialogTitle>
        <DialogDescription>
          {paro.linea_nombre} · {rangoParo(paro)}
        </DialogDescription>
      </DialogHeader>
      <DialogContent className="flex flex-col gap-3">
        <p className="rounded border border-border bg-surface-overlay px-3 py-2 text-xs text-fg-secondary">
          La máquina registró la hora del paro; usted indica por qué paró. Las horas no se
          modifican.
        </p>
        <CausalPicker
          causales={causales}
          causalMadreId={causalMadreId}
          causalId={causalId}
          onCausalMadreChange={setCausalMadreId}
          onCausalChange={setCausalId}
          idPrefix="completar-paro"
        />
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="completar-paro-obs">
            Observaciones{requiereObs ? ' (obligatorio)' : ' (opcional)'}
          </Label>
          <Textarea
            id="completar-paro-obs"
            value={observaciones}
            maxLength={500}
            onChange={(e) => setObservaciones(e.target.value)}
            placeholder="Detalle del paro"
          />
        </div>
        {error || serverError ? (
          <p role="alert" className="rounded bg-crit-soft px-3 py-2 text-xs text-crit">
            {error ?? serverError}
          </p>
        ) : null}
      </DialogContent>
      <DialogFooter>
        <Button
          variant="outline"
          size={touch ? 'touch' : undefined}
          onClick={onClose}
          disabled={guardando}
        >
          Cancelar
        </Button>
        <Button size={touch ? 'touch' : undefined} onClick={guardar} loading={guardando}>
          Guardar causal
        </Button>
      </DialogFooter>
    </Dialog>
  );
}
